import { useMemo, useState } from 'react';
import {
  Button,
  Callout,
  CopyIcon,
  Label,
  MailIcon,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  TextInput,
  XIcon,
} from './ui';
import { t } from '../i18n';
import { ApiError, api } from '../services/api';
import {
  buildTenantInviteEmail,
  type InviteEmailContent,
} from '../utils/inviteEmail';

export type TenantInviteReady = {
  tenantId: number;
  inviteUrl: string;
  email: string;
  tenantName: string;
  recipientName: string;
  inviteEmail?: InviteEmailContent;
};

type Props = {
  invite: TenantInviteReady | null;
  onClose: () => void;
};

export function TenantInviteReadyModal({ invite, onClose }: Props) {
  const labels = t().platform;
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<string | null>(null);

  const emailContent: InviteEmailContent | null = useMemo(() => {
    if (!invite) return null;
    if (invite.inviteEmail) return invite.inviteEmail;
    return buildTenantInviteEmail({
      recipientName: invite.recipientName,
      companyName: invite.tenantName,
      inviteUrl: invite.inviteUrl,
    });
  }, [invite]);

  async function copyLink() {
    if (!invite) return;
    try {
      await navigator.clipboard.writeText(invite.inviteUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  async function sendEmail() {
    if (!invite) return;
    setSending(true);
    setSendError(null);
    setSendSuccess(null);
    try {
      const result = await api.sendTenantInviteEmail(invite.tenantId);
      setSendSuccess(
        labels.inviteEmailSent.replace('{email}', result.email),
      );
    } catch (err) {
      setSendError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal
      isOpen={Boolean(invite)}
      onClose={() => {
        setSendError(null);
        setSendSuccess(null);
        onClose();
      }}
      size="lg"
    >
      <ModalHeader>
        <ModalTitle>{labels.inviteReadyTitle}</ModalTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label={labels.closeInvite}
        >
          <XIcon className="h-4 w-4" />
        </Button>
      </ModalHeader>
      <ModalContent className="space-y-4">
        <p className="text-sm text-muted">{labels.inviteReadyHint}</p>
        {sendSuccess ? (
          <Callout variant="success">{sendSuccess}</Callout>
        ) : null}
        {sendError ? <Callout variant="danger">{sendError}</Callout> : null}
        {invite && emailContent ? (
          <>
            <div className="rounded-lg border border-line bg-surface/50 px-3 py-3 text-sm">
              <p>
                <span className="text-muted">{labels.companyName}: </span>
                <span className="font-medium">{invite.tenantName}</span>
              </p>
              <p className="mt-1">
                <span className="text-muted">{labels.adminEmail}: </span>
                <span className="font-medium">{invite.email}</span>
              </p>
              <p className="mt-1">
                <span className="text-muted">{labels.inviteSubject}: </span>
                <span className="font-medium">{emailContent.subject}</span>
              </p>
            </div>

            <div>
              <Label>{labels.inviteEmailPreview}</Label>
              <div className="overflow-hidden rounded-xl border border-line bg-[#e8eef4]">
                <iframe
                  title={labels.inviteEmailPreview}
                  srcDoc={emailContent.html}
                  className="h-[26rem] w-full border-0"
                  sandbox=""
                />
              </div>
            </div>

            <div>
              <Label>{labels.inviteLink}</Label>
              <TextInput
                value={invite.inviteUrl}
                readOnly
                showClearButton={false}
              />
            </div>
          </>
        ) : null}
      </ModalContent>
      <ModalFooter className="flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => void copyLink()}
        >
          <CopyIcon className="mr-1.5 h-4 w-4" />
          {copied ? labels.copied : labels.copyLink}
        </Button>
        <Button type="button" loading={sending} onClick={() => void sendEmail()}>
          <MailIcon className="h-4 w-4" />
          {labels.sendInviteEmail}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
